/**
 * InterconnectionSankey -- SVG flow diagram showing energy flowing from
 * the project zone through local displacement and cross-border interconnection
 * to neighbor zones, with fossil fuel breakdown.
 */

import { useMemo } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Neighbor {
  neighbor_area_code: string;
  neighbor_name: string;
  eco_geography: string;
  net_flow_gwh: number;
  displaced_generation: Record<string, number>;
  endpoint_dalys: number;
  endpoint_pdfs: number;
  endpoint_usd: number;
}

export interface InterconnectionBreakdown {
  total_unallocated_gwh: number;
  total_displaced_via_interconnector_gwh: number;
  neighbors: Neighbor[];
  total_endpoint_dalys: number;
  total_endpoint_pdfs: number;
  total_endpoint_usd: number;
  methodology: string;
  notes: string[];
}

interface InterconnectionSankeyProps {
  breakdown: InterconnectionBreakdown;
  totalGenerationGwh: number;
}

// ---------------------------------------------------------------------------
// Fuel colors (matching ProjectSummaryCard / GenerationChangeChart)
// ---------------------------------------------------------------------------

const FUEL_COLORS: Record<string, string> = {
  COL: '#6B7280', Coal: '#6B7280', coal: '#6B7280', Lignite: '#6B7280', lignite: '#6B7280',
  Hard_Coal: '#6B7280', hard_coal: '#6B7280', Brown_Coal: '#6B7280', brown_coal: '#6B7280',
  NG: '#F97316', 'Natural Gas': '#F97316', natural_gas: '#F97316',
  Gas: '#F97316', gas: '#F97316', Fossil_Gas: '#F97316', fossil_gas: '#F97316',
  NUC: '#A855F7', Nuclear: '#A855F7', nuclear: '#A855F7',
  OIL: '#92400E', Oil: '#92400E', oil: '#92400E', Petroleum: '#92400E',
  Fossil_Oil: '#92400E', fossil_oil: '#92400E',
  WAT: '#3B82F6', Hydro: '#3B82F6', hydro: '#3B82F6',
  SUN: '#FBBF24', Solar: '#FBBF24', solar: '#FBBF24',
  WND: '#14B8A6', Wind: '#14B8A6', wind: '#14B8A6',
  OTH: '#D1D5DB', Other: '#D1D5DB', other: '#D1D5DB',
  GEO: '#14B8A6', BIO: '#84CC16',
};

function getFuelColor(fuel: string): string {
  if (FUEL_COLORS[fuel]) return FUEL_COLORS[fuel];
  const key = Object.keys(FUEL_COLORS).find(
    (k) => fuel.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? FUEL_COLORS[key] : '#9CA3AF';
}

function getFuelDisplayName(fuel: string): string {
  const map: Record<string, string> = {
    COL: 'Coal', NG: 'Natural Gas', NUC: 'Nuclear', OIL: 'Oil',
    WAT: 'Hydro', SUN: 'Solar', WND: 'Wind', OTH: 'Other',
    GEO: 'Geothermal', BIO: 'Biomass',
    coal: 'Coal', natural_gas: 'Natural Gas', nuclear: 'Nuclear',
    oil: 'Oil', hydro: 'Hydro', solar: 'Solar', wind: 'Wind',
    hard_coal: 'Hard Coal', brown_coal: 'Brown Coal', lignite: 'Lignite',
    fossil_gas: 'Fossil Gas', fossil_oil: 'Fossil Oil', gas: 'Gas',
    Gas: 'Gas', Coal: 'Coal', Oil: 'Oil', Petroleum: 'Petroleum',
    Hard_Coal: 'Hard Coal', Lignite: 'Lignite', Brown_Coal: 'Brown Coal',
    Fossil_Gas: 'Fossil Gas', Fossil_Oil: 'Fossil Oil',
    'Natural Gas': 'Natural Gas', Nuclear: 'Nuclear', Hydro: 'Hydro',
    Solar: 'Solar', Wind: 'Wind', Other: 'Other',
  };
  return map[fuel] ?? fuel.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(gwh: number): string {
  if (Math.abs(gwh) >= 100) return gwh.toFixed(0);
  if (Math.abs(gwh) >= 10) return gwh.toFixed(1);
  if (Math.abs(gwh) >= 1) return gwh.toFixed(2);
  if (Math.abs(gwh) >= 0.01) return gwh.toFixed(3);
  return gwh.toExponential(1);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterconnectionSankey({ breakdown, totalGenerationGwh }: InterconnectionSankeyProps) {
  const {
    neighbors,
    total_unallocated_gwh,
    total_displaced_via_interconnector_gwh,
  } = breakdown;

  // Sort neighbors by displaced flow (largest first), take top 6 to keep the chart readable
  const sortedNeighbors = useMemo(
    () =>
      [...neighbors]
        .sort((a, b) => Math.abs(b.net_flow_gwh) - Math.abs(a.net_flow_gwh))
        .slice(0, 6),
    [neighbors],
  );

  // Collect displaced fuels across all neighbors for the fuel legend
  const allFuels = useMemo(() => {
    const fuels = new Set<string>();
    sortedNeighbors.forEach((n) =>
      Object.keys(n.displaced_generation).forEach((f) => fuels.add(f)),
    );
    return Array.from(fuels);
  }, [sortedNeighbors]);

  // Layout constants
  const W = 740;
  const H = Math.max(280, 80 + sortedNeighbors.length * 44);
  const colLeft = 30;
  const colMid = W * 0.40;
  const colRight = W * 0.72;
  const nodeW = 14;
  const nodeRadius = 3;

  // Compute heights proportional to GWh
  // Local = total generation minus what flowed cross-border
  const crossBorderGwh = total_displaced_via_interconnector_gwh;
  const localGwh = Math.max(0, totalGenerationGwh - crossBorderGwh);
  const totalOut = localGwh + crossBorderGwh;

  // Middle nodes
  const midTop = 40;
  const midBottom = H - 40;
  const midH = midBottom - midTop;

  const localPct = totalOut > 0 ? localGwh / totalOut : 0.5;
  const localNodeH = Math.max(18, midH * localPct);
  const crossNodeH = Math.max(18, midH * (1 - localPct));
  const midGap = Math.max(12, midH - localNodeH - crossNodeH);

  const localNodeY = midTop;
  const crossNodeY = localNodeY + localNodeH + midGap;

  // Left node (project generation) spans the full height
  const leftNodeY = midTop;
  const leftNodeH = midH;

  // Right nodes: one per neighbor, proportional height within the cross-border block
  const totalNeighborGwh = sortedNeighbors.reduce((s, n) => s + Math.abs(n.net_flow_gwh), 0);
  const rightGap = 8;
  const rightAreaTop = crossNodeY;
  const rightAreaH = crossNodeH;
  const totalRightGap = (sortedNeighbors.length - 1) * rightGap;
  const usableRightH = rightAreaH - totalRightGap;

  const rightNodes = useMemo(() => {
    let y = rightAreaTop;
    return sortedNeighbors.map((n) => {
      const pct = totalNeighborGwh > 0 ? Math.abs(n.net_flow_gwh) / totalNeighborGwh : 1 / sortedNeighbors.length;
      const h = Math.max(16, usableRightH * pct);
      const node = { ...n, y, h };
      y += h + rightGap;
      return node;
    });
  }, [sortedNeighbors, rightAreaTop, totalNeighborGwh, usableRightH]);

  // Bezier path helper
  function flowPath(
    x1: number, y1: number, h1: number,
    x2: number, y2: number, h2: number,
  ): string {
    const cx = (x1 + x2) / 2;
    return [
      `M ${x1} ${y1}`,
      `C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`,
      `L ${x2} ${y2 + h2}`,
      `C ${cx} ${y2 + h2}, ${cx} ${y1 + h1}, ${x1} ${y1 + h1}`,
      'Z',
    ].join(' ');
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ minWidth: 480, maxHeight: 360 }}
        role="img"
        aria-label="Interconnection flow diagram"
      >
        <defs>
          <linearGradient id="flow-local" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="flow-cross" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#F97316" stopOpacity="0.3" />
          </linearGradient>
          {rightNodes.map((_, i) => (
            <linearGradient key={i} id={`flow-neighbor-${i}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#F97316" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#F97316" stopOpacity="0.12" />
            </linearGradient>
          ))}
        </defs>

        {/* ---- Flow: Left -> Local Displacement ---- */}
        <path
          d={flowPath(
            colLeft + nodeW, leftNodeY, localNodeH,
            colMid, localNodeY, localNodeH,
          )}
          fill="url(#flow-local)"
        />

        {/* ---- Flow: Left -> Cross-Border ---- */}
        <path
          d={flowPath(
            colLeft + nodeW, leftNodeY + localNodeH, leftNodeH - localNodeH,
            colMid, crossNodeY, crossNodeH,
          )}
          fill="url(#flow-cross)"
        />

        {/* ---- Flows: Cross-Border -> each Neighbor ---- */}
        {rightNodes.map((n, i) => {
          // Sub-portion of the cross-border node that maps to this neighbor
          const pct = totalNeighborGwh > 0 ? Math.abs(n.net_flow_gwh) / totalNeighborGwh : 1 / rightNodes.length;
          const srcH = crossNodeH * pct;
          const srcY = crossNodeY + (i === 0 ? 0 : rightNodes.slice(0, i).reduce((s, prev) => {
            const prevPct = totalNeighborGwh > 0 ? Math.abs(prev.net_flow_gwh) / totalNeighborGwh : 1 / rightNodes.length;
            return s + crossNodeH * prevPct;
          }, 0));

          return (
            <path
              key={n.neighbor_area_code}
              d={flowPath(
                colMid + nodeW, srcY, srcH,
                colRight, n.y, n.h,
              )}
              fill={`url(#flow-neighbor-${i})`}
            />
          );
        })}

        {/* ---- Left node (Project Generation) ---- */}
        <rect
          x={colLeft} y={leftNodeY} width={nodeW} height={leftNodeH}
          rx={nodeRadius}
          className="fill-quantum-400"
        />
        <text
          x={colLeft + nodeW / 2} y={leftNodeY - 10}
          textAnchor="middle"
          className="fill-text-primary text-[11px] font-semibold"
        >
          Project
        </text>
        <text
          x={colLeft + nodeW / 2} y={leftNodeY + leftNodeH / 2}
          textAnchor="middle" dominantBaseline="central"
          className="fill-text-primary text-[9px] font-medium"
          transform={`rotate(-90, ${colLeft + nodeW / 2}, ${leftNodeY + leftNodeH / 2})`}
        >
          {fmt(totalGenerationGwh)} GWh
        </text>

        {/* ---- Middle: Local Displacement node ---- */}
        <rect
          x={colMid} y={localNodeY} width={nodeW} height={localNodeH}
          rx={nodeRadius}
          className="fill-quantum-500/80"
        />
        <text
          x={colMid + nodeW + 8} y={localNodeY + localNodeH / 2}
          dominantBaseline="central"
          className="fill-text-secondary text-[10px]"
        >
          Local: {fmt(localGwh)} GWh
        </text>

        {/* ---- Middle: Cross-Border node ---- */}
        <rect
          x={colMid} y={crossNodeY} width={nodeW} height={crossNodeH}
          rx={nodeRadius}
          fill="#F97316"
          opacity={0.85}
        />
        <text
          x={colMid + nodeW + 8} y={crossNodeY - 8}
          className="fill-text-secondary text-[10px] font-medium"
        >
          Cross-Border: {fmt(crossBorderGwh)} GWh
        </text>

        {/* ---- Right: Neighbor nodes with fuel breakdown ---- */}
        {rightNodes.map((n) => {
          const fuels = Object.entries(n.displaced_generation)
            .filter(([, v]) => Math.abs(v) > 0)
            .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a));
          const totalFuelGwh = fuels.reduce((s, [, v]) => s + Math.abs(v), 0);

          // Stack fuel segments vertically within the node
          let fuelY = n.y;

          return (
            <g key={n.neighbor_area_code}>
              {/* Fuel-stacked bar */}
              {fuels.map(([fuel, gwh]) => {
                const segH = totalFuelGwh > 0 ? (Math.abs(gwh) / totalFuelGwh) * n.h : n.h / fuels.length;
                const y = fuelY;
                fuelY += segH;
                return (
                  <rect
                    key={fuel}
                    x={colRight} y={y} width={nodeW} height={Math.max(2, segH)}
                    rx={1}
                    fill={getFuelColor(fuel)}
                    opacity={0.9}
                  >
                    <title>{getFuelDisplayName(fuel)}: {fmt(Math.abs(gwh))} GWh</title>
                  </rect>
                );
              })}

              {/* Label */}
              <text
                x={colRight + nodeW + 8} y={n.y + n.h / 2}
                dominantBaseline="central"
                className="fill-text-primary text-[10px] font-medium"
              >
                {n.neighbor_name || n.neighbor_area_code}
              </text>
              <text
                x={colRight + nodeW + 8} y={n.y + n.h / 2 + 13}
                dominantBaseline="central"
                className="fill-text-muted text-[9px]"
              >
                {fmt(Math.abs(n.net_flow_gwh))} GWh
                {fuels.length > 0 && ` (${fuels.map(([f]) => getFuelDisplayName(f)).join(', ')})`}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Fuel legend */}
      {allFuels.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-2 px-1">
          {allFuels.map((fuel) => (
            <div key={fuel} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm inline-block"
                style={{ backgroundColor: getFuelColor(fuel) }}
              />
              <span className="text-[10px] text-text-muted">{getFuelDisplayName(fuel)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
